import type { ReactNode } from 'react';
import { useLocale, useT } from '../../../messages';
import { humanize, weekdayName } from './words';

/**
 * An owner's answer, whatever shape it is, **readably**.
 *
 * The old review printed anything that was not a string as JSON in a `<pre>`,
 * on the argument that a renderer per question kind would be a second copy of
 * the form. That argument still holds, so this is not one: it is a renderer
 * per *shape*. Text prints as written (tenant content is never translated or
 * paraphrased), a list is a list, a row of fields is a small table of labels
 * and values, yes/no is a word, and a day of opening hours — the one shape
 * with a meaning the operator should not have to decode — reads "Mon
 * 09:00–17:00". A shape nobody foresaw still reads, as labels and values,
 * because the fallback is the object renderer and not the JSON.
 */
export function AnswerView({ value }: { value: unknown }): ReactNode {
  return <Value value={value} depth={0} />;
}

const MAX_DEPTH = 4;

function Value({ value, depth }: { value: unknown; depth: number }): ReactNode {
  const t = useT();
  const locale = useLocale();

  if (value === null || value === undefined || value === '') {
    return <span className="muted">{t('site.answer.blank')}</span>;
  }
  if (typeof value === 'string') return <span className="site-answer__text">{value}</span>;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? t('domains.yes') : t('domains.no');
  if (depth >= MAX_DEPTH) return <span className="muted">…</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="muted">{t('site.answer.none')}</span>;
    const days = value.every(isDay);
    return (
      <ul className={days ? 'site-answer__week' : 'site-answer__list'}>
        {value.map((item, i) => (
          <li key={i}>
            {isDay(item) ? (
              <Day day={item} locale={locale} />
            ) : (
              <Value value={item} depth={depth + 1} />
            )}
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === 'object') {
    if (isDay(value)) return <Day day={value} locale={locale} />;
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && v !== '',
    );
    if (entries.length === 0) return <span className="muted">{t('site.answer.blank')}</span>;
    return (
      <dl className="site-answer__fields">
        {entries.map(([key, v]) => (
          <div key={key}>
            <dt>{humanize(key)}</dt>
            <dd>
              <Value value={v} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return String(value);
}

interface DayShape {
  weekday: number;
  closed?: boolean;
  windows?: { start: string; end: string }[];
}

function isDay(value: unknown): value is DayShape {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['weekday'] === 'number' && (v['windows'] === undefined || Array.isArray(v['windows']))
  );
}

function Day({ day, locale }: { day: DayShape; locale: string }): ReactNode {
  const t = useT();
  const windows = day.windows ?? [];
  return (
    <>
      <span className="site-answer__day">{weekdayName(day.weekday, locale)}</span>{' '}
      {day.closed === true || windows.length === 0 ? (
        <span className="muted">{t('site.hours.closed')}</span>
      ) : (
        windows.map((w) => `${w.start}–${w.end}`).join(', ')
      )}
    </>
  );
}
